/**
 * LeadsCard Component
 *
 * The dashboard's way into the Leads inbox: how many leads need a follow-up
 * and the latest few. Shown only to organizations that run paid campaigns
 * or already have leads, so organic-only users never see an empty card.
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import type { Lead, LeadsSummary } from '@/types/leads';
import { formatCount, leadSourceLabel } from '@/utils/analyticsFormat';

export interface LeadsCardProps {
  summary: LeadsSummary;
  latest: Lead[];
  onOpenAll: () => void;
  onOpenLead: (lead: Lead) => void;
}

function when(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export const LeadsCard: React.FC<LeadsCardProps> = ({ summary, latest, onOpenAll, onOpenLead }) => (
  <Card variant="outlined" padding="none" className="!rounded-2xl px-5 py-[18px]">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <h3 className="font-display text-[15px] font-bold text-ink">Leads</h3>
      <p className="text-[13px] text-ink-2">
        {summary.new > 0
          ? <><span className="font-bold text-primary">{formatCount(summary.new)} new</span> to follow up · </>
          : null}
        {formatCount(summary.total)} total
        {summary.calls > 0 && <> · {formatCount(summary.calls)} {summary.calls === 1 ? 'call' : 'calls'}</>}
      </p>
      <button type="button" onClick={onOpenAll} className="ml-auto text-[13px] font-bold text-primary hover:underline">
        View all leads
      </button>
    </div>

    {latest.length === 0 ? (
      <p className="mt-2 text-[13px] text-ink-3">
        No leads yet. People who fill a lead form on your Meta, Google or Pinterest ads appear here.
      </p>
    ) : (
      <ul className="mt-3 divide-y divide-border">
        {latest.map((l) => (
          <li key={l.id}>
            <button
              type="button"
              onClick={() => onOpenLead(l)}
              className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-surface/60 rounded-md px-1 -mx-1"
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <span className="truncate">{l.name || l.email || l.phone || 'Lead'}</span>
                  {l.status === 'new' && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="New" />}
                </span>
                <span className="block truncate text-[12px] text-ink-3">
                  {leadSourceLabel(l.platform, l.details.source_platform)}
                  {l.campaign_name && <> · {l.campaign_name}</>}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-ink-3">{when(l.submitted_at)}</span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

export default LeadsCard;
